/* Venix features experience. Loaded with defer by every venix-fx section; runs once. */
(() => {
  if (window.VenixFX) return;
  window.VenixFX = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const small = window.matchMedia('(max-width: 749px)');

  /* ---------- scroll reveals + split headings ---------- */
  const revealIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      revealIO.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });

  function split(el) {
    if (el.dataset.vxSplitDone) return;
    el.dataset.vxSplitDone = '1';
    let i = 0;
    const walk = (node) => {
      [...node.childNodes].forEach((child) => {
        if (child.nodeType === 3) {
          const frag = document.createDocumentFragment();
          child.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
            const w = document.createElement('span');
            w.className = 'w';
            const inner = document.createElement('span');
            inner.style.setProperty('--i', i++);
            inner.textContent = part;
            w.appendChild(inner);
            frag.appendChild(w);
          });
          child.replaceWith(frag);
        } else if (child.nodeType === 1 && child.tagName !== 'BR') {
          walk(child);
        }
      });
    };
    walk(el);
  }

  function initReveal(root) {
    root.querySelectorAll('.vx-split').forEach(split);
    // The hero is the first thing on screen: play its entrance on load. Waiting for the scroll
    // observer left the Buy button invisible below the store header until the visitor scrolled.
    const hero = [...root.querySelectorAll('.vx-hero [data-vx-reveal], .vx-hero .vx-split, .vx-hero .vx-draw')];
    if (hero.length) requestAnimationFrame(() => requestAnimationFrame(() => hero.forEach((el) => el.classList.add('is-in'))));
    root.querySelectorAll('[data-vx-reveal], .vx-split, .vx-draw').forEach((el) => { if (!hero.includes(el)) revealIO.observe(el); });
  }

  /* ---------- timed overlays (Six ways in): [data-at]/[data-until] items show while their clip plays; [data-final] in the end state ---------- */
  function initCues(root) {
    root.querySelectorAll('[data-vx-cues]').forEach((ov) => {
      if (ov.dataset.vxCuesDone) return;
      ov.dataset.vxCuesDone = '1';
      const media = ov.parentElement.querySelector('vx-media, vx-clock');
      const video = media && (media.tagName === 'VX-CLOCK' ? media : media.querySelector('video'));
      const items = [...ov.querySelectorAll('[data-at]')].map((el) => ({
        el,
        at: parseFloat(el.dataset.at),
        until: el.dataset.until ? parseFloat(el.dataset.until) : Infinity,
        fin: el.hasAttribute('data-final'),
      }));
      // the end state matches the still: before the clip starts, after it ends, and without motion
      const paint = () => {
        const final = !video || !video.currentSrc || video.ended || (video.paused && video.currentTime === 0) || media.classList.contains('is-static');
        const t = video ? video.currentTime : 0;
        items.forEach((c) => c.el.classList.toggle('is-on', final ? c.fin : t >= c.at && t < c.until));
      };
      let raf = 0;
      const loop = () => {
        paint();
        raf = video && !video.paused && !video.ended ? requestAnimationFrame(loop) : 0;
      };
      if (video) ['play', 'playing', 'pause', 'ended', 'seeked', 'emptied', 'loadeddata', 'error'].forEach((ev) => video.addEventListener(ev, () => { if (!raf) loop(); }));
      paint();
    });
  }

  /* ---------- Meet Venix: the text and method tiles arrive with the camera at the end of the light tunnel ---------- */
  class VxIntro extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.section = this.closest('.vx-intro') || this;
      this.media = this.querySelector('vx-media');
      this.list = this.querySelector('.vx-intro__methods');
      const video = this.media && this.media.querySelector('video');
      const src = this.media && ((small.matches && this.media.dataset.srcM) || this.media.dataset.srcD);
      if (!video || !src || reduce.matches) { this.still(); return; }
      video.addEventListener('timeupdate', () => { if (video.duration && video.currentTime >= video.duration - 1.2) this.arrive(); });
      this.media.addEventListener('vx:ended', () => this.arrive());
      video.addEventListener('error', () => this.still());
      // replay flies in again: hide the overlay until it lands
      video.addEventListener('play', () => { if (video.currentTime < 0.5 && !small.matches) this.section.classList.remove('is-arrived'); });
      // if the clip never starts (autoplay blocked, slow network), show the end still and the text
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        setTimeout(() => { if (video.paused && video.currentTime === 0) this.still(); }, 6000);
      }, { threshold: 0.35 });
      io.observe(this);
    }
    still() {
      if (this.media) this.media.classList.add('is-static');
      this.arrive();
    }
    arrive() {
      if (this.section.classList.contains('is-arrived')) return;
      this.section.classList.add('is-arrived');
      if (this.list && !this.list.classList.contains('vx-draw')) {
        this.list.classList.add('vx-draw');
        requestAnimationFrame(() => requestAnimationFrame(() => this.list.classList.add('is-in')));
      }
    }
  }
  if (!customElements.get('vx-intro')) customElements.define('vx-intro', VxIntro);

  /* ---------- sticky buy bar: shows once the first buy button has scrolled away, steps aside while another is on screen ---------- */
  class VxBuybar extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.update = this.update.bind(this);
      this.onScreen = new Set();
      this.triggers = [...document.querySelectorAll('.vx-hero .vx-ctas, .vx-end .vx-ctas, product-form, form[action*="/cart/add"]')]
        .filter((el) => !this.contains(el));
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? this.onScreen.add(e.target) : this.onScreen.delete(e.target)));
        this.update();
      });
      this.triggers.forEach((el) => io.observe(el));
      // Buy now links to the product page; on that page itself, bring its Add to cart button into view instead.
      this.addEventListener('click', (e) => {
        const a = e.target.closest('a[data-vx-buy]');
        const atc = document.querySelector('product-form .product-form__submit, form[action*="/cart/add"] [type="submit"]');
        if (!a || !atc || new URL(a.href, location.href).pathname !== location.pathname) return;
        e.preventDefault();
        atc.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        atc.classList.remove('vx-flash');
        requestAnimationFrame(() => atc.classList.add('vx-flash'));
        setTimeout(() => atc.focus({ preventScroll: true }), 600);
      });
      window.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }
    update() {
      const first = this.triggers[0];
      const past = first ? first.getBoundingClientRect().bottom < 0 : window.scrollY > window.innerHeight * 0.6;
      const show = past && this.onScreen.size === 0;
      if (show === this.shown) return;
      this.shown = show;
      this.classList.toggle('is-shown', show);
    }
  }
  if (!customElements.get('vx-buybar')) customElements.define('vx-buybar', VxBuybar);

  /* ---------- media: lazy load, play in view, hold on the end still ---------- */
  const nearIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) { e.target.load(); nearIO.unobserve(e.target); } });
  }, { rootMargin: '900px 0px' });
  const viewIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => e.target.onView(e.intersectionRatio));
  }, { threshold: [0, 0.35] });

  class VxMedia extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.video = this.querySelector('video');
      this.once = this.dataset.play === 'once';
      this.inView = false;
      this.held = true;
      this.replayBtn = this.querySelector('.vx-media__replay');
      if (!this.video) return;
      this.video.addEventListener('playing', () => this.setState('is-playing'));
      this.video.addEventListener('pause', () => { if (!this.video.ended) this.setState('is-paused'); });
      this.video.addEventListener('ended', () => {
        this.setState('is-ended');
        this.dispatchEvent(new CustomEvent('vx:ended', { bubbles: true }));
      });
      if (this.replayBtn) {
        this.replayBtn.hidden = false;
        this.replayBtn.addEventListener('click', (ev) => { ev.stopPropagation(); this.restart(true); });
      }
      if (this.hasAttribute('data-zoom')) {
        this.classList.add('is-zoomable');
        this.addEventListener('click', () => openLightbox(this));
      }
      if (reduce.matches) this.classList.add('is-static');
      nearIO.observe(this);
      viewIO.observe(this);
    }
    setState(cls) {
      this.classList.remove('is-playing', 'is-paused', 'is-ended');
      this.classList.add(cls);
    }
    src() { return (small.matches && this.dataset.srcM) || this.dataset.srcD; }
    load() {
      if (this.loaded || !this.video) return;
      this.loaded = true;
      this.video.src = this.src();
      this.video.preload = 'auto';
    }
    get passive() { return this.closest('[data-vx-switch]') && !this.closest('.is-active'); }
    onView(ratio) {
      const visible = ratio >= 0.35;
      if (ratio === 0 && this.once && this.video && this.video.ended) this.held = true;
      if (visible === this.inView) return;
      this.inView = visible;
      if (visible && !this.passive) this.start();
      else if (!visible) this.pause();
    }
    start() {
      if (!this.video || (reduce.matches && !this.forced)) return;
      this.load();
      if (this.once && this.held) { this.held = false; this.video.currentTime = 0; }
      if (this.once && this.video.ended) return;
      const p = this.video.play();
      if (p) p.catch(() => {});
    }
    restart(force) {
      if (!this.video) return;
      this.forced = !!force;
      this.load();
      this.held = false;
      this.video.currentTime = 0;
      const p = this.video.play();
      if (p) p.catch(() => {});
    }
    pause() { if (this.video && !this.video.paused) this.video.pause(); }
  }
  if (!customElements.get('vx-media')) customElements.define('vx-media', VxMedia);

  /* ---------- vx-clock: a silent timeline for an animated HTML/CSS diagram, played like a clip (in view, once, holds the end) ---------- */
  class VxClock extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.duration = parseFloat(this.dataset.duration) || 8;
      this.currentTime = 0;
      this.paused = true;
      this.ended = false;
      this.held = true;
      this.currentSrc = 'clock';
      this.replayBtn = this.querySelector('.vx-media__replay');
      if (this.replayBtn) {
        this.replayBtn.hidden = false;
        this.replayBtn.addEventListener('click', (ev) => { ev.stopPropagation(); this.restart(true); });
      }
      if (reduce.matches) this.classList.add('is-static');
      viewIO.observe(this);
    }
    get passive() { return this.closest('[data-vx-switch]') && !this.closest('.is-active'); }
    emit(type) { this.dispatchEvent(new Event(type)); }
    load() {}
    onView(ratio) {
      const visible = ratio >= 0.35;
      if (ratio === 0 && this.ended) this.held = true;
      if (visible === this.inView) return;
      this.inView = visible;
      if (visible && !this.passive) this.start();
      else if (!visible) this.pause();
    }
    start() {
      if (reduce.matches && !this.forced) return;
      if (this.held) { this.held = false; this.currentTime = 0; this.ended = false; }
      if (this.ended) return;
      this.play();
    }
    play() {
      if (!this.paused) return;
      this.paused = false;
      this.classList.remove('is-ended');
      this.classList.add('is-playing');
      this.t0 = performance.now() - this.currentTime * 1000;
      this.emit('play');
      const step = (now) => {
        if (this.paused) return;
        this.currentTime = Math.min(this.duration, (now - this.t0) / 1000);
        if (this.currentTime >= this.duration) {
          this.paused = true;
          this.ended = true;
          this.classList.remove('is-playing');
          this.classList.add('is-ended');
          this.emit('ended');
          this.dispatchEvent(new CustomEvent('vx:ended', { bubbles: true }));
          return;
        }
        requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }
    pause() {
      if (this.paused) return;
      this.paused = true;
      this.classList.remove('is-playing');
      this.emit('pause');
    }
    restart(force) {
      this.forced = !!force;
      this.pause();
      this.held = false;
      this.ended = false;
      this.currentTime = 0;
      this.emit('seeked');
      this.play();
    }
  }
  if (!customElements.get('vx-clock')) customElements.define('vx-clock', VxClock);


  /* ---------- switcher: tabs that swap clips, auto-advancing like stories ---------- */
  class VxSwitch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.tabs = [...this.querySelectorAll('[data-vx-tab]')];
      this.panels = [...this.querySelectorAll('[data-vx-panel]')];
      this.captions = [...this.querySelectorAll('[data-vx-caption]')];
      this.auto = this.hasAttribute('data-auto') && !reduce.matches;
      this.index = Math.max(0, this.tabs.findIndex((t) => t.classList.contains('is-active')));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => { this.auto = false; clearTimeout(this.nextTimer); this.show(i, true); });
        tab.addEventListener('keydown', (e) => this.key(e, i));
        // focus on a tab stops auto-advance, so the selected tab never moves away from under the keyboard
        tab.addEventListener('focus', () => { this.auto = false; clearTimeout(this.nextTimer); this.orient(); });
      });
      this.addEventListener('vx:ended', (e) => {
        if (!this.auto || !this.panels[this.index] || !this.panels[this.index].contains(e.target)) return;
        // a visitor's pick during this pause wins: the click clears the timer and turns auto off
        const from = this.index;
        clearTimeout(this.nextTimer);
        this.nextTimer = setTimeout(() => { if (this.auto && this.index === from) this.show((from + 1) % this.panels.length, false); }, 1400);
      });
      this.tick = this.tick.bind(this);
      requestAnimationFrame(this.tick);
      this.show(this.index, false, true);
      this.orient();
    }
    show(i, user, first) {
      this.index = i;
      this.tabs.forEach((t, k) => { t.classList.toggle('is-active', k === i); t.setAttribute('aria-selected', k === i); t.tabIndex = k === i ? 0 : -1; t.style.setProperty('--p', 0); });
      this.panels.forEach((p, k) => {
        p.classList.toggle('is-active', k === i);
        p.toggleAttribute('inert', k !== i);
        const m = p.querySelector('vx-media, vx-clock');
        if (!m) return;
        if (k === i) {
          if (!first) { m.load(); if (m.inView || user) m.restart(user); }
        } else m.pause();
      });
      this.captions.forEach((c, k) => { c.hidden = k !== i; });
      if (user && this.tabs[i]) this.tabs[i].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
    // keyboard: only the selected tab is in the Tab order; arrows move to the next/previous tab and pick it like a
    // click (Up/Down as well when the list is laid out as a column), Home/End jump to the first/last
    key(e, i) {
      const n = this.tabs.length;
      const col = this.orient() === 'vertical';
      let to = -1;
      if (e.key === 'ArrowRight' || (col && e.key === 'ArrowDown')) to = (i + 1) % n;
      else if (e.key === 'ArrowLeft' || (col && e.key === 'ArrowUp')) to = (i - 1 + n) % n;
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = n - 1;
      if (to < 0 || e.altKey || e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      // Home on the first tab, End on the last: already there, so don't restart its clip
      if (to === i) return;
      this.tabs[to].focus();
      this.tabs[to].click();
    }
    // the same list can be a row on phones and a column on desktop, so read it from the list's layout: a row of tabs
    // that wraps onto several lines on a narrow screen is still a row
    orient() {
      const [a, b] = this.tabs;
      if (!a || !b) return 'horizontal';
      const list = a.closest('[role="tablist"]');
      const cs = getComputedStyle(list || a.parentElement);
      let col;
      if (cs.display.includes('flex')) col = cs.flexDirection.startsWith('column');
      else if (cs.display.includes('grid')) col = cs.gridAutoFlow.startsWith('row') && cs.gridTemplateColumns.trim().split(/\s+/).length === 1;
      else {
        const ra = a.getBoundingClientRect();
        const rb = b.getBoundingClientRect();
        col = Math.abs(rb.top - ra.top) > Math.abs(rb.left - ra.left);
      }
      const o = col ? 'vertical' : 'horizontal';
      if (list) list.setAttribute('aria-orientation', o);
      return o;
    }
    tick() {
      const tab = this.tabs[this.index];
      const m = this.panels[this.index] && this.panels[this.index].querySelector('video, vx-clock');
      if (tab && m && m.duration) tab.style.setProperty('--p', Math.min(1, m.currentTime / m.duration).toFixed(4));
      if (this.isConnected) requestAnimationFrame(this.tick);
    }
  }
  if (!customElements.get('vx-switch')) customElements.define('vx-switch', VxSwitch);

  /* ---------- count-up numbers ---------- */
  const countIO = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      countIO.unobserve(e.target);
      const el = e.target;
      const to = parseFloat(el.dataset.vxCount);
      const fmt = new Intl.NumberFormat('en-US');
      if (reduce.matches || !isFinite(to)) return;
      const t0 = performance.now();
      const dur = 1600;
      const step = (now) => {
        const k = Math.min(1, (now - t0) / dur);
        const eased = 1 - Math.pow(1 - k, 4);
        el.textContent = fmt.format(Math.round(to * eased));
        if (k < 1) requestAnimationFrame(step);
      };
      el.textContent = '0';
      requestAnimationFrame(step);
    });
  }, { threshold: 0.6 });

  /* ---------- scroll scrub (exploded view) ---------- */
  class VxScrub extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.img = this.querySelector('[data-vx-frame]');
      this.frames = JSON.parse(this.dataset.frames || '[]');
      this.steps = [...this.querySelectorAll('[data-vx-step]')];
      if (reduce.matches || !this.frames.length) {
        this.classList.add('is-static');
        if (this.frames.length) this.img.src = this.frames[this.frames.length - 1];
        this.steps.forEach((s) => s.classList.add('is-on'));
        return;
      }
      this.cache = [];
      const io = new IntersectionObserver((es) => {
        if (es[0].isIntersecting) { this.preload(); io.disconnect(); }
      }, { rootMargin: '1200px 0px' });
      io.observe(this);
      this.update = this.update.bind(this);
      window.addEventListener('scroll', this.update, { passive: true });
      window.addEventListener('resize', this.update);
      this.update();
    }
    preload() { this.cache = this.frames.map((src) => { const im = new Image(); im.decoding = 'async'; im.src = src; return im; }); }
    update() {
      const r = this.getBoundingClientRect();
      const span = r.height - window.innerHeight;
      const p = Math.min(1, Math.max(0, -r.top / Math.max(1, span)));
      this.style.setProperty('--p', p.toFixed(4));
      const i = Math.min(this.frames.length - 1, Math.round(p * (this.frames.length - 1)));
      if (this.cache[i] && this.cache[i].complete && this.current !== i) { this.img.src = this.cache[i].src; this.current = i; }
      const n = this.steps.length;
      const on = Math.min(n - 1, Math.floor(p * n * 1.0001));
      this.steps.forEach((s, k) => s.classList.toggle('is-on', k === on));
    }
  }
  if (!customElements.get('vx-scrub')) customElements.define('vx-scrub', VxScrub);

  /* ---------- sticky chapter nav: offset under the theme header, scrollspy, progress ---------- */
  class VxNav extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.list = this.querySelector('[data-vx-links]');
      this.chapters = [...document.querySelectorAll('[data-vx-nav]')];
      this.list.innerHTML = '';
      this.links = this.chapters.map((ch) => {
        const a = document.createElement('a');
        a.href = '#' + ch.id;
        a.textContent = ch.dataset.vxNav;
        this.list.appendChild(a);
        return a;
      });
      this.header = document.querySelector('.section-header');
      this.spy = this.spy.bind(this);
      window.addEventListener('scroll', this.spy, { passive: true });
      window.addEventListener('resize', this.spy);
      this.spy();
    }
    spy() {
      let top = 0;
      if (this.header) {
        const b = this.header.getBoundingClientRect().bottom;
        const sticky = getComputedStyle(this.header).position === 'sticky' || this.header.classList.contains('shopify-section-header-sticky');
        if (sticky && b > 0) top = Math.round(b);
      }
      if (top !== this.top) {
        this.top = top;
        document.querySelectorAll('.vx').forEach((s) => s.style.setProperty('--vx-top', top + 'px'));
      }
      // Each Shopify section has its own wrapper, so the bar is pinned with position: fixed
      // between the nav's own slot and the end of the features.
      const endEl = document.querySelector('[data-vx-end]');
      const slot = this.getBoundingClientRect().top;
      const past = endEl ? endEl.getBoundingClientRect().bottom < top + 60 : false;
      this.classList.toggle('is-fixed', slot <= top && !past);
      this.classList.toggle('is-gone', past);
      const mark = top + 140;
      let active = -1;
      this.chapters.forEach((ch, i) => { if (ch.getBoundingClientRect().top <= mark) active = i; });
      this.links.forEach((a, i) => a.classList.toggle('is-active', i === active));
      if (active !== this.active && this.links[active]) {
        this.active = active;
        const l = this.links[active];
        this.list.scrollTo({ left: l.offsetLeft - this.list.clientWidth / 2 + l.clientWidth / 2, behavior: 'smooth' });
      }
      const first = this.chapters[0];
      const last = document.querySelector('[data-vx-end]') || this.chapters[this.chapters.length - 1];
      if (first && last) {
        const start = first.getBoundingClientRect().top + window.scrollY - mark;
        const end = last.getBoundingClientRect().bottom + window.scrollY - window.innerHeight;
        const p = Math.min(1, Math.max(0, (window.scrollY - start) / Math.max(1, end - start)));
        this.style.setProperty('--p', p.toFixed(4));
      }
    }
  }
  if (!customElements.get('vx-nav')) customElements.define('vx-nav', VxNav);

  /* ---------- rail arrows ---------- */
  function initRails(root) {
    root.querySelectorAll('[data-vx-rail]').forEach((wrap) => {
      if (wrap.dataset.vxReady) return;
      wrap.dataset.vxReady = '1';
      const rail = wrap.querySelector('.vx-rail');
      wrap.querySelectorAll('[data-vx-dir]').forEach((b) => b.addEventListener('click', () => {
        rail.scrollBy({ left: Number(b.dataset.vxDir) * rail.clientWidth * 0.8, behavior: 'smooth' });
      }));
    });
  }

  /* ---------- FAQ: smooth open/close ---------- */
  function initFaq(root) {
    root.querySelectorAll('.vx-faq details').forEach((d) => {
      if (d.dataset.vxReady) return;
      d.dataset.vxReady = '1';
      const sum = d.querySelector('summary');
      const body = d.querySelector('.vx-faq__a');
      sum.addEventListener('click', (e) => {
        if (reduce.matches) return;
        e.preventDefault();
        if (d.open) {
          const a = body.animate([{ height: body.scrollHeight + 'px' }, { height: '0px' }], { duration: 380, easing: 'cubic-bezier(.2,.75,0,1)' });
          a.onfinish = () => { d.open = false; };
        } else {
          d.open = true;
          body.animate([{ height: '0px', opacity: 0 }, { height: body.scrollHeight + 'px', opacity: 1 }], { duration: 520, easing: 'cubic-bezier(.2,.75,0,1)' });
        }
      });
    });
  }

  /* ---------- lightbox ---------- */
  let box;
  function openLightbox(media) {
    if (!box) {
      box = document.createElement('dialog');
      box.className = 'vx vx-lightbox';
      box.innerHTML = '<button type="button" aria-label="Close">&times;</button><video controls playsinline muted></video>';
      document.body.appendChild(box);
      box.querySelector('button').addEventListener('click', () => box.close());
      box.addEventListener('click', (e) => { if (e.target === box) box.close(); });
      box.addEventListener('close', () => box.querySelector('video').pause());
    }
    const v = box.querySelector('video');
    v.src = media.dataset.srcD;
    v.setAttribute('aria-label', media.getAttribute('aria-label') || '');
    box.showModal();
    const p = v.play();
    if (p) p.catch(() => {});
  }

  /* ---------- hero pointer parallax ---------- */
  function initHero(root) {
    root.querySelectorAll('[data-vx-parallax]').forEach((hero) => {
      if (hero.dataset.vxReady || reduce.matches || !window.matchMedia('(pointer: fine)').matches) return;
      hero.dataset.vxReady = '1';
      const layers = hero.querySelectorAll('[data-vx-depth]');
      hero.addEventListener('pointermove', (e) => {
        const r = hero.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        layers.forEach((l) => { const d = Number(l.dataset.vxDepth); l.style.translate = `${(x * d).toFixed(1)}px ${(y * d).toFixed(1)}px`; });
      });
    });
  }

  function init(root = document) {
    document.documentElement.classList.add('vx-js');
    initReveal(root);
    initCues(root);
    root.querySelectorAll('[data-vx-count]').forEach((el) => countIO.observe(el));
    initRails(root);
    initFaq(root);
    initHero(root);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
